import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Truck, Store, MapPin, Home, Briefcase, CreditCard, Banknote, FileText, Loader2, ShieldCheck, ChevronDown, ChevronUp, Ban, Clock, Gift } from 'lucide-react';
import { formatGs } from '@/utils/helpers';

const ICON_MAP = { casa: Home, hogar: Home, trabajo: Briefcase, oficina: Briefcase };
function getLabelIcon(label) {
  const lower = (label || '').toLowerCase();
  for (const [key, Icon] of Object.entries(ICON_MAP)) if (lower.includes(key)) return Icon;
  return MapPin;
}

export default function OrderConfirmModal({ open, onClose, onEdit, onConfirm, orderData }) {
  const [show, setShow] = useState(false);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) requestAnimationFrame(() => setShow(true));
    else setShow(false);
  }, [open]);

  const handleConfirm = useCallback(async () => {
    setSending(true);
    try { await onConfirm(); }
    finally { setSending(false); }
  }, [onConfirm]);

  if (!open) return null;

  const {
    orderType, selectedAddr, activeZone, pickupBranch, branches,
    metodo, nota, items, subtotal, couponDiscount, loyaltyDiscount,
    appliedCoupon, loyalty, loyaltyStatus, effectiveDelivery, finalTotal, user,
    addressRef, excludedItems, eligibleItems
  } = orderData;

  const isDelivery = orderType === 'delivery';
  const activeBranch = isDelivery
    ? branches?.find(b => b.id === activeZone?.branch_id)
    : branches?.find(b => b.id === pickupBranch);
  const branchName = isDelivery ? activeZone?.branch_name : activeBranch?.name;
  const estimatedTime = activeBranch?.estimated_delivery_time_minutes || 45;
  const arrivalTime = new Date(Date.now() + estimatedTime * 60 * 1000);
  const arrivalStr = arrivalTime.toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Asuncion' });
  const AddrIcon = selectedAddr ? getLabelIcon(selectedAddr?.label) : MapPin;
  const totalDiscount = couponDiscount + loyaltyDiscount;
  const hasExcluded = excludedItems?.length > 0;
  const showAllItems = expanded || items.length <= 4;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9990] flex items-end sm:items-center justify-center transition-colors duration-300 ${show ? 'bg-black/70 backdrop-blur-sm' : 'bg-black/0 pointer-events-none'}`}
      onClick={onClose}
    >
      <div
        data-testid="order-confirm-modal"
        className={`w-full max-w-lg bg-zinc-950 sm:rounded-2xl rounded-t-3xl shadow-[0_-8px_40px_rgba(0,0,0,0.6)] overflow-hidden transition-all duration-400 ${show ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}
        style={{ maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Accent bar */}
        <div className="h-1 bg-gradient-to-r from-[#E84C1C] via-orange-400 to-[#E84C1C]" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div>
            <h2 className="text-base font-bold">Revisá tu pedido</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">Confirmá los datos antes de enviar</p>
          </div>
          <button data-testid="confirm-modal-close" onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700">
            <X size={16} className="text-zinc-400" />
          </button>
        </div>

        {/* Scrollable content */}
        <div ref={scrollRef} className="px-5 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 160px)' }}>
          {/* Order type + branch */}
          <div className="flex gap-3 mb-3">
            <div className="flex-1 p-3 rounded-xl bg-zinc-900 border border-zinc-800/40">
              <div className="flex items-center gap-2 mb-1">
                {isDelivery ? <Truck size={14} className="text-[#E84C1C]" /> : <Store size={14} className="text-[#E84C1C]" />}
                <span className="text-[11px] text-zinc-500 uppercase tracking-wider">{isDelivery ? 'Delivery' : 'Retiro'}</span>
              </div>
              <p className="text-sm font-medium truncate">{branchName || 'Sucursal'}</p>
            </div>
            <div className="flex-1 p-3 rounded-xl bg-zinc-900 border border-zinc-800/40">
              <div className="flex items-center gap-2 mb-1">
                {metodo === 'efectivo' ? <Banknote size={14} className="text-[#E84C1C]" /> : <CreditCard size={14} className="text-[#E84C1C]" />}
                <span className="text-[11px] text-zinc-500 uppercase tracking-wider">Pago</span>
              </div>
              <p className="text-sm font-medium">{metodo === 'efectivo' ? 'Efectivo' : 'Tarjeta POS'}</p>
            </div>
          </div>

          {/* Address (delivery only) */}
          {isDelivery && selectedAddr && (
            <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-800/40 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-[#E84C1C]/10 flex items-center justify-center shrink-0">
                  <AddrIcon size={16} className="text-[#E84C1C]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{selectedAddr.label || 'Mi dirección'}</p>
                  <p className="text-xs text-zinc-400 truncate">{addressRef || selectedAddr.address_text || selectedAddr.address_line}</p>
                </div>
              </div>
            </div>
          )}

          {/* Estimated delivery/pickup time */}
          <div data-testid="estimated-time" className="flex items-center gap-3 px-1 mb-3">
            <Clock size={14} className="text-amber-400 shrink-0" />
            <div className="text-sm text-zinc-300">
              <span>{isDelivery ? 'Entrega estimada' : 'Retiro estimado'}: <span className="font-semibold text-amber-400">{estimatedTime} min</span></span>
              <span className="text-zinc-500 ml-2">({arrivalStr} hs)</span>
            </div>
          </div>

          {/* Loyalty info */}
          {loyaltyStatus?.active && (
            <div data-testid="confirm-loyalty" className={`flex items-center gap-3 px-1 mb-3`}>
              <Gift size={14} className={loyaltyStatus.reward_available ? 'text-emerald-400 shrink-0' : 'text-amber-400 shrink-0'} />
              <div className="text-sm">
                {loyaltyStatus.reward_available && loyaltyDiscount > 0 ? (
                  <span className="text-emerald-400 font-medium">Tu {loyaltyStatus.reward_value}% OFF aplicado ({loyaltyStatus.remaining_uses} uso{loyaltyStatus.remaining_uses !== 1 ? 's' : ''} restante{loyaltyStatus.remaining_uses !== 1 ? 's' : ''})</span>
                ) : loyaltyStatus.reward_available ? (
                  <span className="text-emerald-400 font-medium">Tu {loyaltyStatus.reward_value}% OFF está disponible ({loyaltyStatus.remaining_uses} uso{loyaltyStatus.remaining_uses !== 1 ? 's' : ''})</span>
                ) : loyaltyStatus.remaining_orders === 1 ? (
                  <span className="text-amber-400 font-medium">Este pedido desbloquea tu {loyaltyStatus.reward_value}% OFF</span>
                ) : (
                  <span className="text-zinc-400">Llevás {loyaltyStatus.total_orders}/{loyaltyStatus.required_orders} pedidos. Te faltan {loyaltyStatus.remaining_orders} para {loyaltyStatus.reward_value}% OFF</span>
                )}
              </div>
            </div>
          )}

          {/* Customer info */}
          {user && (
            <div className="flex gap-2 text-xs text-zinc-400 mb-3 px-1">
              {user.nombre && <span>{user.nombre}</span>}
              {user.telefono && <><span className="text-zinc-600">|</span><span>{user.telefono}</span></>}
            </div>
          )}

          {/* Products */}
          <div className="mb-3">
            <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2 px-1">Productos ({items.length})</p>
            <div className="space-y-1.5">
              {(showAllItems ? items : items.slice(0, 3)).map(i => {
                const addonsCost = i.addons.reduce((s, a) => s + a.extra_cost, 0);
                const lineTotal = (i.unit_price + addonsCost) * i.quantity;
                const isExcluded = excludedItems?.some(e => e.key === i.key);
                return (
                  <div key={i.key} data-testid={`confirm-item-${i.product_id}`} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-zinc-900/60 border border-zinc-800/30">
                    <span className="text-xs font-bold text-[#E84C1C] mt-0.5 shrink-0 w-5 text-center">{i.quantity}x</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight">{i.name}</p>
                      {i.variant_name && <p className="text-[11px] text-zinc-400 mt-0.5">{i.variant_name}</p>}
                      {i.addons.length > 0 && <p className="text-[11px] text-zinc-500 mt-0.5">+ {i.addons.map(a => a.name).join(', ')}</p>}
                      {isExcluded && <span className="inline-flex items-center gap-1 text-[10px] text-amber-400/80 mt-1"><Ban size={9} /> Sin descuento</span>}
                    </div>
                    <span className="text-sm font-medium shrink-0">{formatGs(lineTotal)}</span>
                  </div>
                );
              })}
              {!showAllItems && items.length > 3 && (
                <button onClick={() => setExpanded(true)} className="w-full flex items-center justify-center gap-1 py-2 text-xs text-[#E84C1C] hover:text-[#ff6040] transition-colors">
                  <ChevronDown size={14} /> Ver {items.length - 3} productos más
                </button>
              )}
              {expanded && items.length > 4 && (
                <button onClick={() => setExpanded(false)} className="w-full flex items-center justify-center gap-1 py-1 text-xs text-zinc-500 hover:text-zinc-300">
                  <ChevronUp size={14} /> Menos
                </button>
              )}
            </div>
          </div>

          {/* Note */}
          {nota && (
            <div className="p-2.5 rounded-lg bg-zinc-900/60 border border-zinc-800/30 mb-3">
              <div className="flex items-start gap-2">
                <FileText size={13} className="text-zinc-500 mt-0.5 shrink-0" />
                <p className="text-xs text-zinc-400 italic">"{nota}"</p>
              </div>
            </div>
          )}

          {/* Excluded from discount info */}
          {hasExcluded && totalDiscount > 0 && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15 mb-3">
              <Ban size={13} className="text-amber-400 mt-0.5 shrink-0" />
              <p className="text-[11px] text-amber-400/80">
                {excludedItems.length} producto{excludedItems.length > 1 ? 's' : ''} excluido{excludedItems.length > 1 ? 's' : ''} de descuento (categoría sin descuento).
              </p>
            </div>
          )}

          {/* Totals */}
          <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-800/40 space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-zinc-400">Subtotal</span><span>{formatGs(subtotal)}</span></div>
            {couponDiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-emerald-400">Cupón ({appliedCoupon?.code})</span>
                <span className="text-emerald-400">-{formatGs(couponDiscount)}</span>
              </div>
            )}
            {loyaltyDiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-amber-400">Fidelidad ({loyalty?.discount_percentage}%)</span>
                <span className="text-amber-400">-{formatGs(loyaltyDiscount)}</span>
              </div>
            )}
            {isDelivery && (
              <div className="flex justify-between text-sm"><span className="text-zinc-400">Envío</span><span>{formatGs(effectiveDelivery)}</span></div>
            )}
            <div className="border-t border-zinc-800 pt-2 mt-1 flex justify-between items-center">
              <span className="font-semibold">Total</span>
              <span className="text-lg font-bold text-[#E84C1C]">{formatGs(finalTotal)}</span>
            </div>
          </div>
        </div>

        {/* Action buttons - always visible */}
        <div className="px-5 py-4 border-t border-zinc-800/50 bg-zinc-950 space-y-2">
          <button
            data-testid="confirm-modal-send"
            onClick={handleConfirm}
            disabled={sending}
            className="w-full py-3.5 rounded-2xl bg-[#E84C1C] text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#d43f12] active:scale-[0.98] transition-all shadow-[0_0_24px_rgba(232,76,28,0.3)] disabled:opacity-50"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
            {sending ? 'Enviando...' : 'Confirmar y enviar'}
          </button>
          <div className="flex gap-2">
            <button
              data-testid="confirm-modal-edit"
              onClick={onEdit}
              className="flex-1 py-2.5 rounded-xl text-sm text-zinc-300 border border-zinc-800 hover:bg-zinc-800/50 transition-colors font-medium"
            >
              Editar pedido
            </button>
            <button
              data-testid="confirm-modal-cancel"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm text-zinc-500 border border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
